import { Checkbox, Stack, Typography } from '@mui/material';
import {
  BackupStatus,
  Provider,
  ResourceType,
  type BackupVault,
  type InventoryResource,
} from '@repo/api-gw-sdk';
import { useEffect, useMemo, useState } from 'react';

import { MainDialog } from '@/components/dialogs/mainDialog';
import { AllRegionsSelector } from '@/components/selectors/allRegionsSelector';
import { BackupVaultSelection } from '@/components/vaults/backupVaultSelection';
import { useDAL } from '@/contexts/useDAL';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useUser } from '@/contexts/useUser';
import { useWorkspace } from '@/contexts/useWorkspace';
import { PreferencesKey } from '@/data/dal/preferencesKey';
import { useBackupVaults } from '@/data/vaults/useBackupVaults';

import {
  areAllNativeBackupResources,
  isNativeCrossRegionUnsupported,
} from './inventoryResourceUtils';

import { RetentionSelector } from '../settings/controls/retentionSelector';

interface TakeSnapshotDialogProps {
  entities: InventoryResource[];
  onClose: () => void;
}

const INITIAL_RETENTION_DAYS = 30;

export function TakeSnapshotDialog(props: TakeSnapshotDialogProps) {
  const { entities, onClose } = props;
  const { EnableAzureVaultNetworkIsolation } = useFeatureFlags();
  const { snackbar } = useWorkspace();
  const [inProgress, setInProgress] = useState(false);
  const [pitrSnapshot, setPitrSnapshot] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [retentionInDays, setRetentionInDays] = useState(
    INITIAL_RETENTION_DAYS
  );
  const [selectedVault, setSelectedVault] = useState<BackupVault | undefined>();
  const [targetRegion, setTargetRegion] = useState<string>(entities[0].region);
  const dal = useDAL();
  const { vaults, isLoading } = useBackupVaults();

  // Check if all resources support native backup (e.g., FSx/EFS use AWS Backup in customer account)
  const isNativeBackup = areAllNativeBackupResources(entities);

  const hasCrossRegionRestriction = entities.some(
    isNativeCrossRegionUnsupported
  );

  const providerVaults = useMemo(() => {
    // Native backups (FSx/EFS) don't use Eon vaults
    if (isNativeBackup) {
      return [];
    }

    const isDlsg = entities[0].backupStatus === BackupStatus.Dlsg;
    if (isDlsg) {
      return vaults.filter(
        (vault) =>
          vault.id === entities[0].dlsgPolicy?.policyPlan.backupSchedule.vaultId
      );
    }
    return vaults.filter((vault) => {
      const isNonIsolatedNetwork =
        !EnableAzureVaultNetworkIsolation &&
        vault.vaultAttributes.cloudProvider === Provider.Azure &&
        entities.every(
          (x) =>
            x.resourceType === ResourceType.AwsS3 ||
            x.resourceType === ResourceType.GcpCloudStorageBucket
        );

      return (
        isNonIsolatedNetwork ||
        vault.vaultAttributes.cloudProvider === entities[0].cloudProvider ||
        entities[0].cloudProvider === Provider.MongoAtlas
      );
    });
  }, [vaults, entities, EnableAzureVaultNetworkIsolation, isNativeBackup]);

  useEffect(() => {
    // Auto-select first vault for non-native backups only
    if (!isNativeBackup && providerVaults?.length > 0) {
      setSelectedVault(providerVaults[0]);
    }
  }, [providerVaults, isNativeBackup]);

  const triggerTakeSnapshot = async () => {
    // For non-native backups, vault is required
    if (!isNativeBackup && !selectedVault) {
      return;
    }

    setInProgress(true);
    setError(undefined);

    try {
      await Promise.all(
        entities.map((resource) => {
          if (
            resource.backupStatus === BackupStatus.Terminated ||
            resource.backupStatus === BackupStatus.Disconnected ||
            resource.backupStatus === BackupStatus.Unsupported ||
            resource.backupStatus === BackupStatus.LimitExceeded
          ) {
            return Promise.resolve();
          }

          // Use native backup for FSx/EFS - stored in customer's AWS Backup Vault
          if (isNativeBackup) {
            // Only pass targetRegion if different from source (cross-region backup)
            const crossRegionTarget =
              targetRegion !== resource.region ? targetRegion : undefined;
            return dal.jobs.backup.createNative(
              resource.id,
              retentionInDays,
              crossRegionTarget
            );
          }

          return dal.jobs.backup.create(
            resource.id,
            selectedVault!.id,
            retentionInDays,
            pitrSnapshot
          );
        })
      );
      snackbar.showMessage(
        `Backup job started for ${entities.map((x) => x.resourceName).join(', ')}`
      );
      onClose();
    } catch (error) {
      const err = error as Error;
      setError(err.message || 'Something went wrong...');
    } finally {
      setInProgress(false);
    }
  };

  // Determine if the button should be disabled
  // For native backups (FSx/EFS), vault selection is not required
  const isButtonDisabled = isNativeBackup
    ? isLoading || retentionInDays < 1
    : isLoading || !selectedVault || retentionInDays < 1;

  // Dynamic subtitle based on resource type
  const dialogSubtitle = isNativeBackup
    ? hasCrossRegionRestriction
      ? 'Native backups are stored in your AWS Backup Vault. Cross-region backup is not supported for this resource type.'
      : 'Native backups are stored in your AWS Backup Vault. Select a different region for cross-region backup.'
    : 'Choose the retention period and vault for the snapshot.';

  const { isEmployee } = useUser();
  const { isDemo } = useEnvironment();
  const { data: prefResponse } = dal.preferences.useUserPref(
    PreferencesKey.Preferences
  );

  const devMode = isEmployee && !isDemo && prefResponse?.value?.isDevMode;

  return (
    <>
      <MainDialog
        isOpen={true}
        isLoading={isLoading}
        header={{
          title: 'Take on-demand snapshot',
          subtitle: dialogSubtitle,
          onClose,
        }}
        footer={{
          primaryButton: {
            'data-testid': 'take-snapshot-button',
            icon: 'material-symbols-backup-outline-rounded',
            text: 'Take Snapshot',
            disabled: isButtonDisabled,
            onClick: triggerTakeSnapshot,
          },
          secondaryButton: {
            'data-testid': 'cancel-take-snapshot-button',
            onClick: onClose,
            text: 'Cancel',
          },
          submitInProgress: inProgress,
          loadingText: 'Taking snapshot...',
          children: error ? (
            <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
              <i className='ri-error-warning-line mr-[8px] text-[18px]' />
              {error}
            </Stack>
          ) : null,
        }}
      >
        <Stack gap='24px'>
          <Stack gap='12px' paddingX='40px'>
            <Stack direction='row' gap='24px'>
              <Stack gap='12px' flex={1}>
                <Typography>Retention (calculated from now)</Typography>
                <RetentionSelector
                  onChange={setRetentionInDays}
                  retentionInDays={INITIAL_RETENTION_DAYS}
                />
              </Stack>
              {isNativeBackup && (
                <Stack gap='12px' flex={1}>
                  <Typography>Region (in source account)</Typography>
                  <AllRegionsSelector
                    regionName={targetRegion}
                    setRegionName={setTargetRegion}
                    provider={entities[0].cloudProvider}
                    disabled={hasCrossRegionRestriction}
                  />
                </Stack>
              )}
            </Stack>
          </Stack>
          {devMode &&
            entities.length === 1 &&
            entities[0].resourceType === ResourceType.AzureSapHanaVm && (
              <Stack gap='12px' paddingX='40px'>
                <Typography>Hana pitr snapshot</Typography>
                <Checkbox
                  checked={pitrSnapshot}
                  onChange={(e) => setPitrSnapshot(e.target.checked)}
                />
              </Stack>
            )}
          {!isNativeBackup && (
            <Stack gap='12px'>
              <Typography paddingLeft='40px'>Choose a vault:</Typography>
              <Stack
                width='100%'
                overflow='auto'
                padding='4px 24px 4px 40px'
                marginY='12px'
                direction='row'
                gap='24px'
              >
                <BackupVaultSelection
                  onSelect={setSelectedVault}
                  selectedVaults={selectedVault ? [selectedVault.id] : []}
                  loading={isLoading}
                  vaults={providerVaults}
                  shouldWrap={false}
                />
              </Stack>
            </Stack>
          )}
        </Stack>
      </MainDialog>
    </>
  );
}
